import React, { Fragment } from 'react';
import { observer } from 'mobx-react';
import { http } from '../../services/HttpInterceptor';
import {
    RouteComponentProps
} from "react-router-dom";
import { toast } from 'react-toastify';
import { observable } from 'mobx';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import Pagination from "react-js-pagination";
import { sessionService } from '../../services/SessionService';

registerLocale('es', es);
setDefaultLocale('es');

interface MonthlyFacturasProps extends RouteComponentProps {
    changeToggle(action?: boolean): void
}

@observer export default class MonthlyFacturas extends React.Component<MonthlyFacturasProps> {

    @observable private isLoaded = false;
    @observable private loading = false;
    @observable private allGeneradas: any[] = [];
    @observable private allCanceladas: any[] = [];
    @observable private filter: 'todas' | 'generadas' | 'canceladas' = 'todas';
    @observable private fromDate: Date = new Date();
    @observable private page: number = 1;
    private itemsPerPage = 50;

    componentDidMount() {
        this.props.changeToggle(true);
        if (!sessionService.canViewSection("Facturas.view")) {
            toast.error("No tienes permisos para acceder a esta sección");
            this.props.history.push("/facturas");
            return;
        }
        this.loadFacturas();
    }

    private getMonthLabel = (): string => {
        const label = format(this.fromDate, "MMMM yyyy", { locale: es });
        return label.charAt(0).toUpperCase() + label.slice(1);
    }

    private loadFacturas = () => {
        this.loading = true;
        Promise.all([
            http.get('/api/payment/read_facturas'),
            http.get('/api/payment/read_facturas_canceladas')
        ]).then(([generadasRes, canceladasRes]) => {
            this.allGeneradas = generadasRes.data.response.data || [];
            this.allCanceladas = canceladasRes.data.response.data || [];
            this.page = 1;
            this.isLoaded = true;
        }).catch(() => {
            this.allGeneradas = [];
            this.allCanceladas = [];
            this.isLoaded = true;
            toast.error("Error al cargar las facturas");
        }).finally(() => {
            this.loading = false;
        });
    }

    private matchesSelectedPeriod = (dateValue: string): boolean => {
        if (!dateValue) {
            return false;
        }
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) {
            return false;
        }
        return date.getMonth() === this.fromDate.getMonth() && date.getFullYear() === this.fromDate.getFullYear();
    }

    private getGeneradasDelMes = (): any[] => {
        return this.allGeneradas.filter((f) => this.matchesSelectedPeriod(f.cfdi_date));
    }

    private getCanceladasDelMes = (): any[] => {
        return this.allCanceladas.filter((f) => this.matchesSelectedPeriod(f.deleted_date));
    }

    private getFilteredFacturas = (): any[] => {
        const generadas = this.getGeneradasDelMes();
        const canceladas = this.getCanceladasDelMes();

        if (this.filter === 'generadas') {
            return generadas.map((f) => ({ ...f, _status: 'generada' }));
        }
        if (this.filter === 'canceladas') {
            return canceladas.map((f) => ({ ...f, _status: 'cancelada' }));
        }
        return [
            ...generadas.map((f) => ({ ...f, _status: 'generada' })),
            ...canceladas.map((f) => ({ ...f, _status: 'cancelada' }))
        ];
    }

    private getPaginatedFacturas = (): any[] => {
        const filtered = this.getFilteredFacturas();
        const start = (this.page - 1) * this.itemsPerPage;
        return filtered.slice(start, start + this.itemsPerPage);
    }

    private changePage = (page: number) => {
        this.page = page;
    }

    private setStartDate = (date: any) => {
        this.fromDate = date || new Date();
        this.page = 1;
    }

    private setFilter = (filter: 'todas' | 'generadas' | 'canceladas') => {
        this.filter = filter;
        this.page = 1;
    }

    private formatDate = (date: string): string => {
        if (!date) {
            return '-';
        }
        return format(new Date(date), 'dd/MM/yyyy HH:mm');
    }

    private isCancelled = (factura: any): boolean => {
        return factura._status === 'cancelada';
    }

    private getDate = (factura: any): string => {
        if (this.isCancelled(factura)) {
            return factura.deleted_date;
        }
        return factura.cfdi_date;
    }

    render() {
        if (!this.isLoaded && this.loading) {
            return (
                <div className="row">
                    <div className="col-md-12 text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Cargando...</span>
                        </div>
                        <p>Cargando facturas...</p>
                    </div>
                </div>
            );
        }

        const generadasDelMes = this.getGeneradasDelMes();
        const canceladasDelMes = this.getCanceladasDelMes();
        const totalGeneradas = generadasDelMes.length;
        const totalCanceladas = canceladasDelMes.length;
        const totalMes = totalGeneradas + totalCanceladas;
        const filtered = this.getFilteredFacturas();
        const paginated = this.getPaginatedFacturas();
        const totalFiltered = filtered.length;

        return (
            <Fragment>
                <div className="row">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-header card-header-primary">
                                <h4 className="card-title">Facturas del mes</h4>
                                <p className="card-category">
                                    Generadas y canceladas — {this.getMonthLabel()}
                                </p>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-sm-10" style={{ position: 'relative', zIndex: 20 }}>
                                        <DatePicker
                                            selected={this.fromDate}
                                            onChange={date => this.setStartDate(date)}
                                            dateFormat="MM/yyyy"
                                            showMonthYearPicker
                                            locale="es"
                                        />
                                    </div>
                                    <div className="col-sm-2">
                                        <button
                                            className="btn btn-info btn-sm pull-right"
                                            onClick={this.loadFacturas}
                                            disabled={this.loading}
                                        >
                                            <i className="material-icons">refresh</i> Actualizar
                                        </button>
                                    </div>
                                </div>

                                <div className="row mt-3">
                                    <div className="col-md-4">
                                        <div className="card card-stats">
                                            <div className="card-header card-header-info card-header-icon">
                                                <div className="card-icon">
                                                    <i className="material-icons">receipt_long</i>
                                                </div>
                                                <p className="card-category">Total del mes</p>
                                                <h3 className="card-title">{totalMes}</h3>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="card card-stats">
                                            <div className="card-header card-header-success card-header-icon">
                                                <div className="card-icon">
                                                    <i className="material-icons">check_circle</i>
                                                </div>
                                                <p className="card-category">Generadas</p>
                                                <h3 className="card-title">{totalGeneradas}</h3>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="card card-stats">
                                            <div className="card-header card-header-danger card-header-icon">
                                                <div className="card-icon">
                                                    <i className="material-icons">cancel</i>
                                                </div>
                                                <p className="card-category">Canceladas</p>
                                                <h3 className="card-title">{totalCanceladas}</h3>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row mt-2">
                                    <div className="col-md-12">
                                        <button
                                            className={`btn btn-sm ${this.filter === 'todas' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => this.setFilter('todas')}
                                            style={{ marginRight: 5 }}
                                        >
                                            Todas ({totalMes})
                                        </button>
                                        <button
                                            className={`btn btn-sm ${this.filter === 'generadas' ? 'btn-success' : 'btn-outline-success'}`}
                                            onClick={() => this.setFilter('generadas')}
                                            style={{ marginRight: 5 }}
                                        >
                                            Generadas ({totalGeneradas})
                                        </button>
                                        <button
                                            className={`btn btn-sm ${this.filter === 'canceladas' ? 'btn-danger' : 'btn-outline-danger'}`}
                                            onClick={() => this.setFilter('canceladas')}
                                        >
                                            Canceladas ({totalCanceladas})
                                        </button>
                                    </div>
                                </div>

                                {this.loading && (
                                    <div className="row mt-3">
                                        <div className="col-md-12 text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Cargando...</span>
                                            </div>
                                            <p>Cargando facturas...</p>
                                        </div>
                                    </div>
                                )}

                                {!this.loading && totalFiltered === 0 && (
                                    <div className="row mt-3">
                                        <div className="col-md-12 text-center">
                                            <p className="text-muted">No hay facturas para mostrar en este filtro.</p>
                                        </div>
                                    </div>
                                )}

                                {!this.loading && totalFiltered > 0 && (
                                    <div className="row mt-3">
                                        <div className="col-md-12">
                                            {totalFiltered > this.itemsPerPage && (
                                                <div className="row">
                                                    <Pagination
                                                        activePage={this.page}
                                                        itemsCountPerPage={this.itemsPerPage}
                                                        totalItemsCount={Number(totalFiltered)}
                                                        pageRangeDisplayed={5}
                                                        onChange={this.changePage.bind(this)}
                                                        activeClass="btn-warning"
                                                        itemClass="btn btn-round btn-info"
                                                        innerClass="btn-group paginador"
                                                        disabledClass="deshabilitado"
                                                    />
                                                </div>
                                            )}
                                            <div className="table-responsive">
                                                <table className="table">
                                                    <thead className="text-primary">
                                                        <tr>
                                                            <th>CFDI</th>
                                                            <th>
                                                                {this.filter === 'canceladas'
                                                                    ? 'Fecha de cancelación'
                                                                    : this.filter === 'generadas'
                                                                        ? 'Fecha de creación'
                                                                        : 'Fecha'}
                                                            </th>
                                                            {this.filter === 'todas' && <th>Estatus</th>}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginated.map((factura: any, index: any) => {
                                                            const cancelled = this.isCancelled(factura);
                                                            return (
                                                                <tr key={index} className={cancelled ? 'rejected' : 'approved'}>
                                                                    <td>{factura.cfdi || '-'}</td>
                                                                    <td>{this.formatDate(this.getDate(factura))}</td>
                                                                    {this.filter === 'todas' && (
                                                                        <td>
                                                                            {cancelled ? (
                                                                                <span className="text-danger">
                                                                                    <i className="material-icons" style={{ fontSize: 16, verticalAlign: 'middle' }}>cancel</i>
                                                                                    {' '}Cancelada
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-success">
                                                                                    <i className="material-icons" style={{ fontSize: 16, verticalAlign: 'middle' }}>check_circle</i>
                                                                                    {' '}Generada
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                    )}
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {totalFiltered > this.itemsPerPage && (
                                                <div className="row">
                                                    <Pagination
                                                        activePage={this.page}
                                                        itemsCountPerPage={this.itemsPerPage}
                                                        totalItemsCount={Number(totalFiltered)}
                                                        pageRangeDisplayed={5}
                                                        onChange={this.changePage.bind(this)}
                                                        activeClass="btn-warning"
                                                        itemClass="btn btn-round btn-info"
                                                        innerClass="btn-group paginador"
                                                        disabledClass="deshabilitado"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }
}
